import { Box, Grid, Stack, Typography } from "@mui/material"
import useMountEffect from "utils/hooks/useMountEffect"
import { getFeelingOptionsForClientGroup, getFeelingTargetsForClientGroup } from "utils/apiRequests/fetchFeelings"
import { FeelingModel, GroupedFeelings, FeelingTargetModel, UserFeelingModel } from "services/openapi"
import { useMemo, useState } from "react"
import useApiRequestError from "utils/hooks/useApiRequestErrorHandling"
import { useTranslation } from "react-i18next"
import React from "react"

type FeelingListProps = {
  feelings: GroupedFeelings[]
  targets?: UserFeelingModel[]
}

type OutputObject = {
  [key: number]: number[]
}

function aggregateFeelings(input: UserFeelingModel[]): Record<number, Array<[targetId: number, count: number]>> {
  const countsByFeeling: Record<number, Record<number, number>> = {}

  input.forEach((entry) => {
    entry.feelings.forEach((feeling) => {
      const { feelingId, feelingTargetIds } = feeling

      countsByFeeling[feelingId] ??= {}

      feelingTargetIds.forEach((targetId) => {
        countsByFeeling[feelingId][targetId] = (countsByFeeling[feelingId][targetId] ?? 0) + 1
      })
    })
  })

  const result: Record<number, Array<[targetId: number, count: number]>> = {}

  Object.entries(countsByFeeling).forEach(([feelingIdStr, targetCounts]) => {
    const feelingId = Number(feelingIdStr)

    result[feelingId] = Object.entries(targetCounts)
      .map(([targetIdStr, count]) => [Number(targetIdStr), count] as [targetId: number, count: number])
      .sort((a, b) => b[1] - a[1] || a[0] - b[0]) // highest occurrence first, tie -> smaller id first
  })

  return result
}

const FeelingList = ({ feelings, targets }: FeelingListProps) => {
  const [feelingOptions, setFeelingOptions] = useState<FeelingModel[]>([])
  const [feelingTargets, setFeelingTargets] = useState<FeelingTargetModel[]>([])
  const [isLoading, setLoading] = useState<boolean>(true)
  const [openTargetsByFeeling, setOpenTargetsByFeeling] = useState<Record<number, boolean>>({})
  const { handleError } = useApiRequestError()
  const { t } = useTranslation()

  /*
  var total = 0
  for (var i in feelings) {
    total += feelings[i].sum > 0 ? feelings[i].sum : feelings[i].total
  }
  */

  useMountEffect(async function fetchData() {
    await getFeelingOptionsForClientGroup(undefined, setLoading, setFeelingOptions, handleError)
    await getFeelingTargetsForClientGroup(undefined, setLoading, setFeelingTargets, handleError)
  })

  const userFeelingTargets = useMemo<Record<number, Array<[targetId: number, count: number]>>>(
    () => (targets ? aggregateFeelings(targets) : {}),
    [targets]
  )

  const feelingList = feelings?.map((feeling) => {
    let feelingDetails
    //const amount = total === 0 ? "" : `${(((feeling.sum > 0 ? feeling.sum : feeling.total) / total) * 100).toFixed(0)}%`

    if (feelingOptions !== undefined) {
      feelingDetails = feelingOptions.find((x) => x.id === feeling.feelingId)
    }

    const userFeelingTargetDescription = userFeelingTargets[feeling.feelingId]?.map(([targetId, count]) => {
      const feelingTargetDetails = feelingTargets.find((x) => x.id === targetId)
      return t(`youth-view.feeling-selection.feeling-target-options.${feelingTargetDetails?.name}`) + ` (${count})`
    })

    const feelingElement: JSX.Element = (
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}>
        <img
          src={`${process.env.PUBLIC_URL}/feelingIcons/${feelingDetails?.imageLocation}`}
          width={20}
          alt={`${feelingDetails?.imageLocation}`}
          aria-hidden="true"
        />
        <Typography variant={"subtitle2"} sx={{ pl: 1 }}>
          {t(`youth-view.feeling-selection.feeling-options.${feelingDetails?.name}`)}
        </Typography>
      </Box>
    )

    if (targets) {
      return (
        <React.Fragment key={feeling.feelingId.toString()}>
          <Grid key={feeling.feelingId.toString() + "-feeling"} item xs={4} md={2}>
            {feelingElement}
          </Grid>

          <Grid key={feeling.feelingId.toString() + "-target"} item xs={8} md={10}>
            {userFeelingTargetDescription?.length > 0 && (
              <Stack>
                {userFeelingTargetDescription.slice(0, 3).map((target, index) => (
                  <Typography key={index} variant={"subtitle2"}>
                    {target}
                  </Typography>
                ))}

                {userFeelingTargetDescription.length > 3 && (
                  <Box
                    component="details"
                    onToggle={(e: React.SyntheticEvent<HTMLDetailsElement>) => {
                      const details = e.currentTarget as HTMLDetailsElement
                      setOpenTargetsByFeeling((prev) => ({
                        ...prev,
                        [feeling.feelingId]: details.open,
                      }))
                    }}
                    sx={{
                      mt: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      // keep the native disclosure marker (arrow)
                      "& > summary": {
                        cursor: "pointer",
                        display: "list-item",
                      },
                      "& > summary::-webkit-details-marker": {
                        display: "inline-block",
                      },
                      // when open, move the summary (show more/less) under the expanded content
                      "&[open] > summary": {
                        order: 2,
                        mt: 0.5,
                      },
                      "&[open] > .more-content": {
                        order: 1,
                      },
                    }}
                  >
                    <Box component="summary">
                      <Typography component="span" variant="subtitle2" sx={{ textDecoration: "underline" }}>
                        {openTargetsByFeeling[feeling.feelingId]
                          ? t("feelings-list.show-less")
                          : t("feelings-list.show-more")}
                      </Typography>
                    </Box>

                    <Stack className="more-content" sx={{ mt: 0.5 }}>
                      {userFeelingTargetDescription.slice(3).map((target, index) => (
                        <Typography key={index + 3} variant={"subtitle2"}>
                          {target}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            )}
          </Grid>
        </React.Fragment>
      )
    }

    return (
      <Box key={feeling.feelingId.toString()} sx={{ width: "95vw" }}>
        {feelingElement}
      </Box>
    )
  })

  if (targets) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={4} md={2}>
          <Typography variant={"subtitle2"}>{t("feelings-list.feelings").toUpperCase()}</Typography>
        </Grid>
        <Grid item xs={8} md={10}>
          <Typography variant={"subtitle2"}>{t("feelings-list.reasons").toUpperCase()}</Typography>
        </Grid>
        {isLoading ? <></> : feelingList}
      </Grid>
    )
  }

  return (
    <Box sx={{ mt: 0, display: "flex", justifyContent: "flex-start", flexWrap: "wrap" }}>
      {isLoading ? <></> : feelingList}
    </Box>
  )
}

export default FeelingList
